import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faBuilding, faLocationDot } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { Organization } from "../../../Apollo/schema/get-people";

/**
 * Whether the company identity card has anything to reveal behind its
 * "See more" expander (description, HQ/other offices, industry-behind-the-pill,
 * founded year or the associated-profiles count).
 */
export const companyHeaderHasDetails = (
	organization: Organization | undefined,
	industryBehindPill: boolean
): boolean =>
	Boolean(
		organization?.shortDescription?.trim() ||
		organization?.seoDescription?.trim() ||
		organization?.companyLocations?.length ||
		(industryBehindPill && organization?.industry) ||
		organization?.foundedYear ||
		(organization?.employeesFrom &&
			organization?.employeesTo &&
			organization?.estimatedNumEmployees)
	);

const DetailRow: FunctionComponent<{ label: string; value?: string | null }> = ({
	label,
	value,
}) => {
	if (!value) {
		return null;
	}

	return (
		<div className="flex flex-col gap-0.5">
			<div className="text-xs font-semibold uppercase text-text-meta">{label}</div>
			<div className="text-sm text-text">{value}</div>
		</div>
	);
};

/**
 * The detail sections (HQ + other offices, industry, founded year, associated
 * profiles) rendered inside the expanded company identity card, below the
 * About text. Mirrors the info the Overview tab's "About {company}" section
 * used to show before it moved here.
 */
export const CompanyHeaderExpanded: FunctionComponent<{
	organization?: Organization;
	/** True when line 2 shows the category pill, so industry moves down here. */
	showIndustry: boolean;
}> = ({ organization, showIndustry }) => {
	const { t } = useTranslation();

	const hqLocation =
		organization?.city && organization?.country
			? `${organization.city}, ${organization.country}`
			: organization?.city || organization?.country;
	const otherOffices = organization?.companyLocations ?? [];

	return (
		<>
			{(hqLocation || otherOffices.length > 0) && (
				<div className="flex flex-col gap-2">
					<div className="text-xs font-semibold uppercase text-text-meta">
						{t("contacts.leadPanel.identity.locations")}
					</div>
					{hqLocation && (
						<div className="flex items-center gap-2 text-sm text-text">
							<Icon className="h-4 w-4 text-gray-400" icon={faBuilding} />
							<span>
								{hqLocation}
								{" (HQ)"}
							</span>
						</div>
					)}
					{otherOffices.length > 0 && (
						<div className="flex items-start gap-2 text-sm text-text-meta">
							<Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" icon={faLocationDot} />
							<div className="flex flex-col gap-1">
								<span className="text-xs text-text-meta">
									{t("contacts.leadPanel.identity.otherOffices")}
								</span>
								<div className="flex flex-wrap gap-1.5">
									{otherOffices.map((location, index) => (
										<span
											key={`${location.city ?? ""}-${location.country ?? ""}-${index}`}
											className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700"
										>
											{location.city ? `${location.city}, ` : ""}
											{location.country}
										</span>
									))}
								</div>
							</div>
						</div>
					)}
				</div>
			)}
			{showIndustry && (
				<DetailRow label={t("contacts.leadPanel.industry")} value={organization?.industry} />
			)}
			<DetailRow
				label={t("contacts.leadPanel.foundedYear")}
				value={organization?.foundedYear ? String(organization.foundedYear) : null}
			/>
			<DetailRow
				label={t("contacts.leadPanel.identity.associatedProfiles")}
				value={
					organization?.employeesFrom &&
					organization?.employeesTo &&
					organization?.estimatedNumEmployees
						? organization.estimatedNumEmployees.toLocaleString()
						: null
				}
			/>
		</>
	);
};
